import { Actor, ActorSubclass } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import { v4 } from 'uuid'
import { config } from '../appConfig'
import {
  Address,
  idlFactory,
  NamedAddress,
  _SERVICE as PlugbookFactory,
} from '../idls/plugbook/plugbook.did'
import { Chain, Contact } from '../interfaces/common'

export const PLUGBOOK_CANISTER_ID = '4x37z-jyaaa-aaaap-qpznq-cai'

interface NamedAddressUpdate {
  name?: string
  profilePic?: string
  bio?: string
  addresses?: Address[]
}

export interface PlugbookRecord {
  name: string
  bio?: string
  owner: Principal
  addresses: Address[]
  profilePic?: string
}

class PlugbookClient {
  public actor: ActorSubclass<PlugbookFactory>

  constructor() {
    this.actor = Actor.createActor(idlFactory, {
      canisterId: PLUGBOOK_CANISTER_ID,
      agent: config.getAgent(),
    }) as ActorSubclass<PlugbookFactory>
  }

  async autocomplete(search: string) {
    const names = await this.actor.autocomplete(search)
    return names.map(this.convertNamedAddress)
  }

  private convertNamedAddress(namedAddress: NamedAddress): PlugbookRecord {
    const { name, bio, owner, addresses, profile_pic } = namedAddress

    return {
      name,
      owner,
      addresses,
      bio: bio[0],
      profilePic: profile_pic[0],
    }
  }

  async getNamedAddress(name: string) {
    const namedAddressResult = await this.actor.get_named_address_by_name(name)

    if ('Err' in namedAddressResult) throw new Error(namedAddressResult.Err)

    return this.convertNamedAddress(namedAddressResult.Ok)
  }

  async validateName(name: string) {
    const availabilityResult = await this.actor.validate_name(name)

    if ('Err' in availabilityResult) {
      return { valid: false, error: availabilityResult.Err }
    } else {
      return { valid: true, error: null }
    }
  }

  private async getSignedActor(principalId: string) {
    return Actor.createActor(idlFactory, {
      canisterId: PLUGBOOK_CANISTER_ID,
      agent: config.getAgent({ principalId }),
    }) as ActorSubclass<PlugbookFactory>
  }

  async registerUser(principal: string) {
    const actor = await this.getSignedActor(principal)
    const result = await actor.register_user()

    if ('Err' in result) throw new Error(result.Err)

    return this.convertNamedAddress(result.Ok)
  }

  async getNamedAddressForPrincipal(principal: String) {
    const namedAddressResponse = await this.actor.get_named_address_by_owner(
      Principal.from(principal)
    )

    if ('Ok' in namedAddressResponse) return this.convertNamedAddress(namedAddressResponse.Ok)
  }

  async updateNamedAddress(update: NamedAddressUpdate, principalId: string) {
    const signedActor = await this.getSignedActor(principalId)
    const { bio, profilePic, name } = update

    const updateResponse = await signedActor.update_named_address({
      name: name ? [name] : [],
      bio: bio ? [bio] : [],
      profile_pic: profilePic ? [profilePic] : [],
      addresses: update.addresses || [],
    })

    if ('Err' in updateResponse) throw new Error(updateResponse.Err)

    return this.convertNamedAddress(updateResponse.Ok)
  }

  plugbookRecordToContact(record: PlugbookRecord): Contact {
    return {
      id: v4(),
      name: `@${record.name}`,
      addresses: [{ chain: Chain.ICP, address: record.owner.toString() }, ...record.addresses],
      avatar: record.profilePic || '🔌',
    }
  }
}

const plugbookClient = new PlugbookClient()

export default plugbookClient
